import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "width-100"
}
const _hoisted_2 = { class: "full-width flex justify-end" }
const _hoisted_3 = {
  key: 1,
  class: "width-100"
}
const _hoisted_4 = { class: "flex nowrap overflow-auto" }
const _hoisted_5 = ["href"]

import { ref, computed, withDefaults, watch } from "vue";
import { QMarkdown } from "@quasar/quasar-ui-qmarkdown";
import { TypographyProps } from "@/types";
import { logStore, useMargins, useTheme } from "@/hooks";


/**
 * A generic component for displaying text.
 */
const __default__ = {
  name: "Typography",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    value: { default: "" },
    ellipsis: { type: Boolean },
    inheritColor: { type: Boolean },
    error: { type: Boolean },
    secondary: { type: Boolean },
    bold: { type: Boolean },
    wrap: { type: Boolean },
    copyable: { type: Boolean },
    variant: { default: "body" },
    el: { default: "span" },
    align: { default: "left" },
    defaultExpanded: { type: Boolean },
    collapseLength: { default: 500 },
    codeExt: { default: "" },
    color: { default: undefined },
    small: { type: Boolean },
    large: { type: Boolean },
    x: { default: "" },
    y: { default: "" },
    l: { default: "" },
    r: { default: "" },
    t: { default: "" },
    b: { default: "" },
    class: { default: "" }
  },
  setup(__props: any) {

const props = __props;

const { darkMode } = useTheme();

const buttonClassName = useMargins(props);

const className = useMargins(props, () => [
  [props.variant === "large", "text-h3"],
  [props.variant === "title", "text-h4"],
  [props.variant === "subtitle", "text-h5"],
  [props.variant === "small", "text-subtitle2"],
  [props.variant === "caption", "text-caption text-textCaption"],
  [props.variant === "code", "text-body1"],
  [
    props.variant === "body" ||
      props.variant === "expandable" ||
      props.variant === "markdown",
    "text-body1",
  ],
  [props.el === "a", "text-primary"],
  ["align", `text-${props.align}`],
  [!!props.color && !darkMode.value, `text-${props.color}`],
  ["inheritColor", "inherit-color"],
  ["error", "text-negative"],
  ["ellipsis", "text-ellipsis"],
  ["secondary", "text-textCaption"],
  ["bold", "text-bold"],
  ["small", "text-sm"],
  ["large", "text-lg"],
  ["wrap", "text-white-space-normal"],
  ["class", props.class],
]);

const baseExpanded = computed(
  () =>
    props.variant === "markdown" ||
    (props.defaultExpanded &&
      props.variant !== "code" &&
      (props.collapseLength === 0 ||
        String(props.value).length < props.collapseLength))
);

const expanded = ref(baseExpanded.value);

const markdown = computed(
  () => props.variant === "expandable" || props.variant === "markdown"
);

const expandable = computed(() => props.variant === "expandable");

const expandLabel = computed(() => (expanded.value ? "See Less" : "See More"));

const codeExt = computed(() => {
  if (!props.codeExt) {
    return "";
  } else {
    return (
      {
        cpp: "clike",
        cc: "clike",
        c: "clike",
        h: "clike",
        js: "js",
        ts: "js",
        vue: "vue",
        java: "clike",
        py: "python",
      }[props.codeExt] || ""
    );
  }
});

/**
 * Copy the text to the clipboard.
 */
function handleCopy() {
  navigator.clipboard.writeText(String(props.value));
  logStore.onInfo("Copied to clipboard");
}

/**
 * When the variant or value changes, reset the expanded state.
 */
watch(
  () => [props.value, props.variant],
  () => (expanded.value = baseExpanded.value)
);

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_tooltip = _resolveComponent("q-tooltip")!

  return (markdown.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (expanded.value)
          ? (_openBlock(), _createBlock(_unref(QMarkdown), {
              key: 0,
              "no-heading-anchor-links": "",
              src: _ctx.value,
              class: _normalizeClass(_unref(className) + ' text-white-space-normal')
            }, null, 8, ["src", "class"]))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(_unref(className) + ' text-ellipsis text-expanded')
            }, _toDisplayString(_ctx.value), 3)),
        _createElementVNode("div", _hoisted_2, [
          (props.copyable)
            ? (_openBlock(), _createBlock(_component_q_btn, {
                key: 0,
                flat: "",
                dense: "",
                round: "",
                icon: "download",
                size: "sm",
                color: "textCaption",
                class: _normalizeClass(_unref(buttonClassName)),
                onClick: _withModifiers(handleCopy, ["stop"])
              }, null, 8, ["class"]))
            : _createCommentVNode("", true),
          (expandable.value)
            ? (_openBlock(), _createBlock(_component_q_btn, {
                key: 1,
                flat: "",
                dense: "",
                size: "sm",
                color: "textCaption",
                class: _normalizeClass(_unref(buttonClassName)),
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (expanded.value = !expanded.value), ["stop"]))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(expandLabel.value), 1)
                ]),
                _: 1
              }, 8, ["class"]))
            : _createCommentVNode("", true)
        ])
      ]))
    : (_ctx.variant === 'code')
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_q_btn, {
              flat: "",
              dense: "",
              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (expanded.value = !expanded.value), ["stop"]))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_separator, {
                  vertical: "",
                  class: "q-mx-xs"
                }),
                _createVNode(_component_q_tooltip, { delay: 300 }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(expandLabel.value), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            (expanded.value)
              ? (_openBlock(), _createBlock(_unref(QMarkdown), {
                  key: 0,
                  "no-heading-anchor-links": "",
                  class: "full-width"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString("```" + codeExt.value + "\n" + _ctx.value + "\n```"), 1)
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(_unref(className) + ' text-textCaption')
                }, "Code hidden", 2))
          ]),
          _createVNode(_component_q_btn, {
            flat: "",
            size: "sm",
            dense: "",
            color: "textCaption",
            onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (expanded.value = !expanded.value), ["stop"]))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(expandLabel.value), 1)
            ]),
            _: 1
          })
        ]))
      : (_ctx.el === 'span')
        ? (_openBlock(), _createElementBlock("span", {
            key: 2,
            class: _normalizeClass(_unref(className))
          }, _toDisplayString(_ctx.value), 3))
        : (_ctx.el === 'p')
          ? (_openBlock(), _createElementBlock("p", {
              key: 3,
              class: _normalizeClass(_unref(className))
            }, _toDisplayString(_ctx.value), 3))
          : (_ctx.el === 'div')
            ? (_openBlock(), _createElementBlock("div", {
                key: 4,
                class: _normalizeClass(_unref(className))
              }, _toDisplayString(_ctx.value), 3))
            : (_ctx.el === 'h1')
              ? (_openBlock(), _createElementBlock("h1", {
                  key: 5,
                  class: _normalizeClass(_unref(className))
                }, _toDisplayString(_ctx.value), 3))
              : (_ctx.el === 'h2')
                ? (_openBlock(), _createElementBlock("h2", {
                    key: 6,
                    class: _normalizeClass(_unref(className))
                  }, _toDisplayString(_ctx.value), 3))
                : (_ctx.el === 'h3')
                  ? (_openBlock(), _createElementBlock("h3", {
                      key: 7,
                      class: _normalizeClass(_unref(className))
                    }, _toDisplayString(_ctx.value), 3))
                  : (_ctx.el === 'a')
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 8,
                        class: _normalizeClass(_unref(className)),
                        href: _ctx.value,
                        target: "_blank"
                      }, _toDisplayString(_ctx.value), 11, _hoisted_5))
                    : _createCommentVNode("", true)
}
}

})